import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebouncedValue } from "@/shared/hooks/use-debounced-value";
import { CommunicationsPageShell } from "../shared/CommunicationsPageShell";
import { useCommunicationLocales } from "../locales/use-communication-locales";
import type { NotificationChannel } from "../shared/communications-types";
import { useCommunications } from "./use-communications";
import { CommunicationsFilters } from "./CommunicationsFilters";
import { CommunicationsTable } from "./CommunicationsTable";

const SEARCH_DEBOUNCE_MS = 300;

export function CommunicationsPage() {
  const { t } = useTranslation();

  const [keyQuery, setKeyQuery] = useState("");
  const [channel, setChannel] = useState<NotificationChannel | null>(null);
  const [locale, setLocale] = useState<string | null>(null);

  const debouncedKey = useDebouncedValue(keyQuery, SEARCH_DEBOUNCE_MS);

  const filters = useMemo(
    () => ({
      key: debouncedKey || undefined,
      channel: channel ?? undefined,
      locale: locale ?? undefined,
    }),
    [debouncedKey, channel, locale],
  );

  const { data, isLoading, isError } = useCommunications(filters);
  const localesQuery = useCommunicationLocales();

  const handleReset = () => {
    setKeyQuery("");
    setChannel(null);
    setLocale(null);
  };

  return (
    <CommunicationsPageShell
      screenTestId="communications-screen"
      titleKey="communications.catalog.title"
      subtitleKey="communications.catalog.subtitle"
      showTabs
    >
      <CommunicationsFilters
        keyQuery={keyQuery}
        channel={channel}
        locale={locale}
        locales={localesQuery.data ?? []}
        onKeyQueryChange={setKeyQuery}
        onChannelChange={setChannel}
        onLocaleChange={setLocale}
        onReset={handleReset}
      />

      {isLoading && (
        <p data-testid="communications-table-loading" className="text-sm text-muted-foreground">
          {t("communications.catalog.loading")}
        </p>
      )}

      {isError && (
        <p data-testid="communications-error" className="text-sm text-destructive">
          {t("communications.catalog.loadError")}
        </p>
      )}

      {data && data.length === 0 && (
        <p data-testid="communications-empty" className="text-sm text-muted-foreground">
          {t("communications.catalog.empty")}
        </p>
      )}

      {data && data.length > 0 && <CommunicationsTable communications={data} />}
    </CommunicationsPageShell>
  );
}
